import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { ExternalLink, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { photos } from '../mockData';
import MobileTitleSpacer from '../components/MobileTitleSpacer';
import SEO from '../components/SEO';
import { seoConfig } from '../utils/seoConfig';

const Photos = () => {
  // 🔹 Par défaut on affiche "tournage"
  const [activeFilter, setActiveFilter] = useState('tournage');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0); // index dans la liste filtrée

  // refs pour le swipe
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchStartTime = useRef(0);
  const photoWheelRef = useRef(0);
  const photoWheelTimerRef = useRef(null);
  const photoWheelLockRef = useRef(false);

  // 🔹 Filtres : uniquement tournage & perso
  const filters = [
    { id: 'tournage', label: 'Tournage', color: '#4f2b709e' },
    { id: 'perso',    label: 'Perso',    color: '#4f2b709e' },
  ];

  // 🔹 On ne gère plus "all" : on filtre toujours par catégorie
  const filteredPhotos = useMemo(
    () => photos.filter((p) => p.category === activeFilter),
    [activeFilter]
  );

  // Ouvre la lightbox à l’index cliqué
  const openLightbox = (idx) => {
    setCurrentIndex(idx);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden'; // empêcher le scroll derrière
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = '';
  };

  const showPrev = useCallback(() => {
    setCurrentIndex((i) => (i - 1 + filteredPhotos.length) % filteredPhotos.length);
  }, [filteredPhotos.length]);

  const showNext = useCallback(() => {
    setCurrentIndex((i) => (i + 1) % filteredPhotos.length);
  }, [filteredPhotos.length]);

  // Navigation clavier quand la lightbox est ouverte
  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') showPrev();
      if (e.key === 'ArrowRight') showNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen, showPrev, showNext]);

  // Réajuste l’index si le filtre change pendant la lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    if (currentIndex >= filteredPhotos.length) {
      setCurrentIndex(0);
    }
  }, [activeFilter, filteredPhotos.length, lightboxOpen, currentIndex]);

  // Handlers de swipe (mobile)
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
    touchStartTime.current = Date.now();
  };

  const onTouchEnd = (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartX.current;
    const dy = t.clientY - touchStartY.current;
    const dt = Date.now() - touchStartTime.current;

    const minDist = 60; // distance minimale pour valider le swipe
    const mostlyHorizontal = Math.abs(dy) < Math.abs(dx);

    if (mostlyHorizontal && Math.abs(dx) > minDist) {
      if (dx < 0) {
        // swipe vers la gauche -> photo suivante
        showNext();
      } else {
        // swipe vers la droite -> photo précédente
        showPrev();
      }
    }
  };

  // Swipe horizontal au trackpad quand la lightbox est ouverte.
  const onPhotoWheel = (e) => {
    const dx = e.deltaX;
    const dy = e.deltaY;

    if (Math.abs(dx) < 3 || Math.abs(dx) <= Math.abs(dy) * 1.15) return;

    e.preventDefault();

    if (photoWheelLockRef.current) return;

    photoWheelRef.current += dx;

    if (photoWheelTimerRef.current) {
      window.clearTimeout(photoWheelTimerRef.current);
    }
    photoWheelTimerRef.current = window.setTimeout(() => {
      photoWheelRef.current = 0;
    }, 140);

    if (Math.abs(photoWheelRef.current) >= 58) {
      const direction = photoWheelRef.current > 0 ? 1 : -1;
      photoWheelRef.current = 0;
      photoWheelLockRef.current = true;

      if (direction > 0) showNext();
      else showPrev();

      window.setTimeout(() => {
        photoWheelLockRef.current = false;
      }, 470);
    }
  };

  return (
    <>
      <SEO 
        title={seoConfig.photos.title}
        description={seoConfig.photos.description}
        keywords={seoConfig.photos.keywords}
        url={seoConfig.photos.url}
      />
      <div className="min-h-screen bg-black pt-20">
        {/* Header avec photo en fond, comme Studio et Tournage */}
        <div className="relative overflow-hidden">
        <div
          className="
  absolute inset-0 bg-cover bg-center
  md:bg-[length:70%_auto] md:bg-no-repeat
  lg:bg-[length:50%_auto]
  md:[filter:brightness(1.35)]
          "
          style={{
            backgroundImage: "url('/IMAGES/photoselfie.png')",
            backgroundPosition: "center 40%",
            opacity: 1,
          }}
        />
        {/* Fondu progressif de la photo vers le fond noir */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-black" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-20">
          <MobileTitleSpacer height="60px" />

          <h1
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white mb-4"
            style={{ color: '#b67deb9e' }}
          >
            PHOTOS
          </h1>
          <div
            className="h-1 w-32 md:w-40 mb-6"
            style={{ background: 'linear-gradient(to right, #64368f, transparent)' }}
          />
          <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl">
            Portfolio photographique
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 mt-6 mb-8 sm:mb-12">
        <div
          className="flex justify-center flex-nowrap gap-3 sm:gap-4 md:gap-5 overflow-x-auto no-scrollbar"
          style={{
            WebkitOverflowScrolling: 'touch',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
        >
          {filters.map((filter) => {
            const active = activeFilter === filter.id;
            return (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className="
                  flex-shrink-0
                  min-w-[105px] sm:min-w-[120px] md:min-w-[135px]
                  px-5 sm:px-6 md:px-7
                  py-2 sm:py-2.5 md:py-3
                  rounded-xl font-semibold
                  text-xs sm:text-sm md:text-base
                  transition-all duration-300 hover:scale-[1.03]
                  whitespace-nowrap
                "
                style={{
                  backgroundColor: active ? filter.color : 'rgba(168,85,247,0.08)',
                  color: '#fff',
                  border: `1px solid ${
                    active ? filter.color : 'rgba(116, 82, 148, 0.45)'
                  }`,
                  boxShadow: active
                    ? '0 0 20px rgba(168,85,247,0.25)'
                    : 'none',
                }}
              >
                {filter.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Photos Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 sm:pb-16 md:pb-20">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3 sm:gap-5 md:gap-6 lg:gap-8">
          {filteredPhotos.map((photo, idx) => (
            <button
              key={photo.id}
              onClick={() => openLightbox(idx)}
              className="group relative overflow-hidden rounded-2xl aspect-square cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70"
              aria-label={`Agrandir ${photo.title}`}
            >
              <img
                src={photo.url}
                alt={photo.title}
                loading="lazy"
                className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
              />
            </button>
          ))}
        </div>
      </div>

      {/* Lien vers galerie complète */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-20">
        <div className="text-center">
          <a
            href="https://cargocollective.com/charlesludigphotographie"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 sm:gap-3 px-5 sm:px-7 md:px-8 py-2.5 sm:py-3 md:py-4 rounded-full font-bold text-sm sm:text-base md:text-lg transition-all duration-300 transform hover:scale-105 group"
            style={{
              background: 'rgba(255,255,255,0.08)',
              color: '#ffffff',
              border: '1px solid rgba(255,255,255,0.4)',
              boxShadow: '0 0 0 1px rgba(0,0,0,0.4)',
            }}
          >
            <span>Voir la galerie complète</span>
            <ExternalLink
              className="transform group-hover:translate-x-1 transition-transform duration-300"
              size={20}
            />
          </a>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && filteredPhotos.length > 0 && (
        <div
          className="fixed inset-0 z-[10000] bg-black/90 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          {/* Bande de contraste en haut pour bien voir la croix sur mobile */}
          <div className="absolute top-0 left-0 right-0 h-20 bg-gradient-to-b from-black/80 to-transparent pointer-events-none" />

          {/* Container image */}
          <div
            data-page-swipe-lock="true"
            className="relative w-full h-full max-w-[95vw] max-h-[88vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            onWheel={onPhotoWheel}
          >
            {/* Bouton fermer - abaissé sur mobile */}
            <button
              onClick={closeLightbox}
              className="
                absolute top-16 right-3 md:top-20 md:right-6
                flex items-center justify-center
                w-10 h-10 md:w-12 md:h-12
                rounded-full
                bg-black/80 backdrop-blur
                border border-white/50
                shadow-[0_0_0_2px_rgba(0,0,0,0.4)]
                hover:bg-black/90 active:scale-95
                text-white
              "
              aria-label="Fermer"
            >
              <X size={28} />
            </button>

            {/* Flèche gauche (desktop/tablette) */}
            <button
              onClick={showPrev}
              className="hidden sm:flex absolute left-2 md:left-4 items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-black/60 hover:bg-black/75 text-white border border-white/30"
              aria-label="Précédente"
            >
              <ChevronLeft size={28} />
            </button>

            {/* Image */}
            <img
              src={filteredPhotos[currentIndex]?.url}
              alt={filteredPhotos[currentIndex]?.title || 'Photo agrandie'}
              className="max-h-[80vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
            />

            {/* Flèche droite (desktop/tablette) */}
            <button
              onClick={showNext}
              className="hidden sm:flex absolute right-2 md:right-4 items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-black/60 hover:bg-black/75 text-white border border-white/30"
              aria-label="Suivante"
            >
              <ChevronRight size={28} />
            </button>

            {/* Mini contrôles mobiles en bas */}
            <div className="sm:hidden absolute bottom-4 left-0 right-0 flex items-center justify-center gap-4">
              <button
                onClick={showPrev}
                className="px-4 py-2 rounded-full bg_white/10 text-white border border-white/30 bg-white/10"
              >
                Précédente
              </button>
              <button
                onClick={showNext}
                className="px-4 py-2 rounded-full bg-white/10 text-white border border-white/30"
              >
                Suivante
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default Photos;
